import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputBase,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import React, { type ChangeEvent, useEffect, useState } from 'react';
import { Card, RouteDirectory, Title } from 'src/Administration/Common';
import * as Yup from 'yup';
import YupPassword from 'yup-password';
import styles from './EditUserPageStyles';

YupPassword(Yup);

import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import { type FormikHelpers, useFormik } from 'formik';
import allPermissionGroup from 'src/api/permission/allPermissionGroup';
import deleteMediaPermission from 'src/api/permission/deleteMediaPermission';
import newMediaPermission from 'src/api/permission/newMediaPermission';
import updateMediaPermission from 'src/api/permission/updateMediaPermission';
import singleProfile from 'src/api/profile/singleProfile';
import updateProfile from 'src/api/profile/updateProfile';
import { NOT_ADMIN_ROLES, USER_ROLES } from 'src/constants';
import { useCustomToast } from 'src/hooks/menu';
import viewStateVar from 'src/store/viewState';
import { AdminTextInput } from '../Common/input';

const GRID_CONTAINER_SPACING = 4;

interface RoleOption {
  label: string;
  value: string;
}

interface PermissionGroup {
  id: string;
  name: string;
}

interface PermissionGroupNode {
  node: PermissionGroup;
}

interface AllPermissionGroupsData {
  allPermissionGroups: {
    edges: PermissionGroupNode[];
  };
}

interface MediaPermission {
  id?: string;
  canEdit: boolean;
  canDelete: boolean;
  canDownload: boolean;
  canShare: boolean;
  hires: boolean;
  permissionsGroup: PermissionGroup | null;
}

interface MediaPermissionNode {
  node: MediaPermission;
}

interface ProfileData {
  profile: {
    id: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    companyName: string;
    role: string;
    user: {
      username: string;
      email: string;
    };
    mediapermissionSet: {
      edges: MediaPermissionNode[];
    };
  };
}

interface UserFormValues {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  password1: string;
  password2: string;
  phoneNumber: string;
  companyName: string;
  role: string;
}

interface CustomCheckBoxProps {
  label: string;
  defaultChecked?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

interface GroupPermissionsProps {
  name: string;
  a: MediaPermission;
  canEdit?: boolean;
  canDelete?: boolean;
  canShare?: boolean;
  canDownload?: boolean;
  hires?: boolean;
  onSelectPermissionSetting: (name: string, checked: boolean) => void;
  removePermission: () => void;
}

interface PermissionProps {
  selectedPermissions: MediaPermission[];
  addMediaPermissionToList: (node: PermissionGroup) => void;
  removeMediaPermissionFromList: (v: PermissionGroup | null) => void;
  setOptionToMediaPermission: (id: string, name: string, val: boolean) => void;
}

interface PermissionItemProps {
  label: string;
  onClick: () => void;
}

interface PermissionListSearchProps {
  groupsList: AllPermissionGroupsData | undefined;
  addMediaPermissionToList: (node: PermissionGroup) => void;
  selectedPermissions: MediaPermission[];
}

interface EditUserPageComponentProps {
  id: string | null;
}

export default function EditUserPageComponent({ id }: EditUserPageComponentProps): React.ReactElement | null {
  const { data: profileData } = useQuery<ProfileData>(singleProfile, {
    variables: {
      id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  });

  if (!id || !profileData) return null;

  const userMediaPermissionSet = profileData.profile.mediapermissionSet.edges.map((a) => a.node);
  return <EditUserPage profileData={profileData} profileId={id} userMediaPermissionSet={userMediaPermissionSet} />;
}

interface EditUserPageProps {
  profileData: ProfileData;
  profileId: string;
  userMediaPermissionSet: MediaPermission[];
}

function EditUserPage({ profileData, profileId, userMediaPermissionSet }: EditUserPageProps): React.ReactElement {
  const classes = styles();
  const viewState = useReactiveVar(viewStateVar);
  const [selectedPermissions, setSelectedPermissions] = useState<MediaPermission[]>(userMediaPermissionSet);
  const [updateProfileReq] = useMutation(updateProfile);
  const [createMediaPermission] = useMutation(newMediaPermission);
  const [editMediaPermission] = useMutation(updateMediaPermission);
  const [deleteMediaPermissionReq] = useMutation(deleteMediaPermission);
  const navigate = useNavigate();
  const { toastr } = useCustomToast();

  function getRemovedMediaPermissions(): MediaPermission[] {
    return userMediaPermissionSet.filter(
      (a) => !selectedPermissions.find((b) => b?.permissionsGroup?.id === a?.permissionsGroup?.id),
    );
  }

  const formik = useFormik<UserFormValues>({
    initialValues: {
      username: profileData.profile.user.username,
      firstName: profileData.profile.firstName,
      lastName: profileData.profile.lastName,
      email: profileData.profile.user.email,
      password1: '',
      password2: '',
      phoneNumber: profileData.profile.phoneNumber,
      companyName: profileData.profile.companyName,
      role: profileData.profile.role,
    },
    validationSchema: Yup.object({
      username: Yup.string().required('Please provide a username'),
      firstName: Yup.string().required('Please provide a first name'),
      lastName: Yup.string().required('Please provide a last name'),
      email: Yup.string().email('Invalid email format').required('Email is required'),
      password1: Yup.string()
        .min(
          8,
          'password must contain 8 or more characters with at least one of each: uppercase, lowercase, number and special',
        )
        .minLowercase(1, 'password must contain at least 1 lower case letter')
        .minUppercase(1, 'password must contain at least 1 upper case letter')
        .minNumbers(1, 'password must contain at least 1 number')
        .minSymbols(1, 'password must contain at least 1 special character'),
      password2: Yup.string().oneOf([Yup.ref('password1'), ''], 'Passwords must match'),
      role: Yup.string(),
    }),
    onSubmit: async (values: UserFormValues, submitProps: FormikHelpers<UserFormValues>) => {
      const { firstName, lastName, email, password1, password2, role, phoneNumber, companyName } = values;

      const updatePromise =
        password1 === '' || password2 === ''
          ? updateProfileReq({
              variables: {
                profileId,
                firstName,
                lastName,
                email,
                role,
                phoneNumber,
                companyName,
              },
            })
          : updateProfileReq({
              variables: {
                profileId,
                firstName,
                lastName,
                email,
                password1,
                password2,
                role,
                phoneNumber,
                companyName,
              },
            });

      updatePromise
        .then(() => {
          const removedPermissions = getRemovedMediaPermissions();
          for (const { id } of removedPermissions) {
            if (id) {
              deleteMediaPermissionReq({
                variables: {
                  mediaPermissionId: id,
                },
              });
            }
          }

          for (const permission of selectedPermissions) {
            const {
              canDelete = false,
              canDownload = false,
              canEdit = false,
              canShare = false,
              hires = false,
              id,
              permissionsGroup,
            } = permission;

            if (id) {
              editMediaPermission({
                variables: {
                  mediaPermissionId: id,
                  canDelete,
                  canDownload,
                  canEdit,
                  canShare,
                  hires,
                },
              });
            } else {
              createMediaPermission({
                variables: {
                  profileId,
                  permissionsGroupId: permissionsGroup?.id,
                  canDelete,
                  canDownload,
                  canEdit,
                  canShare,
                  hires,
                },
              });
            }
          }
          toastr.success('User updated successfully');
          navigate({ to: '/systemadmin/users' });
          submitProps.resetForm({ values });
        })
        .catch((e: Error) => {
          toastr.error(e.message);
        });
    },
  });

  const onClickCancel = (): void => {
    navigate({ to: '/systemadmin/users' });
  };

  const removeMediaPermissionFromList = (v: PermissionGroup | null): void => {
    setSelectedPermissions((c) => c.filter(({ permissionsGroup }) => permissionsGroup?.id !== v?.id));
  };

  const addMediaPermissionToList = (node: PermissionGroup): void => {
    setSelectedPermissions((prev) => [
      ...prev,
      { permissionsGroup: node, canEdit: false, canDelete: false, canDownload: false, canShare: false, hires: false },
    ]);
  };

  const setOptionToMediaPermission = (id: string, name: string, val: boolean): void => {
    setSelectedPermissions((prev) => prev.map((a) => (a?.permissionsGroup?.id === id ? { ...a, [name]: val } : a)));
  };

  useEffect(() => {
    if (formik.dirty) {
      window.onbeforeunload = (): boolean => true;
    } else {
      window.onbeforeunload = null;
    }
  }, [formik.dirty]);

  return (
    <Box
      sx={{
        width: '95%',
        margin: '0 auto',
        marginTop: '30px',
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container>
          <Grid item xs={12}>
            <RouteDirectory routes={[{ label: 'Users List' }, { label: 'Edit User' }]} />
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <Card style={{}}>
                <Grid item xs={12}>
                  <Title>Edit User</Title>
                </Grid>
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <Grid item xs={6}>
                    <AdminTextInput label={'Username'} disabled {...formik.getFieldProps('username')} />
                    <Typography className={classes.fieldErrors}>{formik.errors.username}</Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <Grid item xs={6}>
                    <AdminTextInput
                      id="cyp-edit-first-name-input"
                      label={'First Name'}
                      {...formik.getFieldProps('firstName')}
                    />
                    <Typography className={classes.fieldErrors}>{formik.errors.firstName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <AdminTextInput
                      id="cyp-edit-last-name-input"
                      label={'Last Name'}
                      {...formik.getFieldProps('lastName')}
                    />
                    <Typography className={classes.fieldErrors}>{formik.errors.lastName}</Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <Grid item xs={6}>
                    <AdminTextInput id="cyp-edit-email-input" label={'Email'} {...formik.getFieldProps('email')} />
                    <Typography className={classes.fieldErrors}>{formik.errors.email}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <div className={classes.cardTitle}>Role</div>
                    <FormControl fullWidth className={classes.selectBox}>
                      <Select
                        labelId="demo-simple-select-label"
                        id="cyp-edit-role-input"
                        {...formik.getFieldProps('role')}
                      >
                        {((viewState.signedInUserRole === 'ADM' ? USER_ROLES : NOT_ADMIN_ROLES) as RoleOption[]).map(
                          ({ label, value }) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <Grid item xs={6}>
                    <AdminTextInput
                      id="cyp-edit-phone-input"
                      label={'Phone'}
                      {...formik.getFieldProps('phoneNumber')}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AdminTextInput
                      id="cyp-edit-company-name-input"
                      label={'Company Name'}
                      {...formik.getFieldProps('companyName')}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <Grid item xs={6}>
                    <AdminTextInput
                      id="cyp-edit-password-input"
                      label={`Password (Must contain 8 or more characters with at least one of each: uppercase, lowercase,
                      number and special)`}
                      type={'password'}
                      {...formik.getFieldProps('password1')}
                    />
                    <Typography className={classes.fieldErrors}>{formik.errors.password1}</Typography>
                  </Grid>
                  <Grid className={classes.confirmPassword} item xs={6}>
                    <AdminTextInput
                      id="cyp-edit-confirm-password-input"
                      label={'Confirm Password'}
                      type={'password'}
                      {...formik.getFieldProps('password2')}
                    />
                    <Typography className={classes.fieldErrors}>{formik.errors.password2}</Typography>
                  </Grid>
                </Grid>
                {formik.getFieldProps('role').value === 'USR' && (
                  <Grid container spacing={GRID_CONTAINER_SPACING}>
                    <Grid
                      item
                      xs={12}
                      style={{
                        paddingBottom: '0',
                      }}
                    >
                      <div className={classes.cardTitle}>Permission</div>
                    </Grid>
                    <Permission
                      setOptionToMediaPermission={setOptionToMediaPermission}
                      addMediaPermissionToList={addMediaPermissionToList}
                      removeMediaPermissionFromList={removeMediaPermissionFromList}
                      selectedPermissions={selectedPermissions}
                    />
                  </Grid>
                )}
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <Grid className={classes.cancelSaveButtons} item xs={12}>
                    <Button
                      id="cyp-edit-user-save-button"
                      disabled={Object.keys(formik.errors).length > 0}
                      variant="contained"
                      color="primary"
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      onClick={onClickCancel}
                      color="secondary"
                      style={{
                        marginLeft: '10px',
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

const Permission = ({
  selectedPermissions,
  addMediaPermissionToList,
  removeMediaPermissionFromList,
  setOptionToMediaPermission,
}: PermissionProps): React.ReactElement => {
  const { data: groupsList } = useQuery<AllPermissionGroupsData>(allPermissionGroup, {
    fetchPolicy: 'network-only',
  });

  return (
    <>
      <Grid item xs={6}>
        <PermissionListSearch
          selectedPermissions={selectedPermissions}
          groupsList={groupsList}
          addMediaPermissionToList={addMediaPermissionToList}
        />
      </Grid>
      <Grid
        item
        xs={6}
        style={{
          overflowY: 'scroll',
        }}
      >
        {selectedPermissions.map((a) => (
          <GroupPermissions
            key={a.id ?? a.permissionsGroup?.id}
            name={a?.permissionsGroup?.name ?? ''}
            a={a}
            canEdit={a.canEdit}
            canDelete={a.canDelete}
            canShare={a.canShare}
            canDownload={a.canDownload}
            hires={a.hires}
            removePermission={() => removeMediaPermissionFromList(a?.permissionsGroup)}
            onSelectPermissionSetting={(name, val) =>
              setOptionToMediaPermission(a?.permissionsGroup?.id ?? '', name, val)
            }
          />
        ))}
      </Grid>
    </>
  );
};

const PermissionListSearch = ({
  groupsList,
  addMediaPermissionToList,
  selectedPermissions,
}: PermissionListSearchProps): React.ReactElement => {
  const classes = styles();
  const [searchVal, setSearchVal] = useState('');

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setSearchVal(e.target.value);
  };

  function isExistInSelectedPermissionList(a: PermissionGroupNode): boolean {
    return !!selectedPermissions.find(({ permissionsGroup }) => permissionsGroup?.id === a?.node?.id);
  }

  const permissionList =
    groupsList?.allPermissionGroups.edges.filter((a) => a.node.name.toUpperCase().includes(searchVal.toUpperCase())) ||
    [];

  return (
    <div className={classes.permissionContainer}>
      <div className={classes.permissionSearchBox}>
        <InputBase
          className={classes.searchInput}
          placeholder={'Quick Search'}
          value={searchVal}
          onChange={onChangeSearch}
        />
      </div>
      <div className={classes.permissionListContainer}>
        {permissionList
          .filter((a) => !isExistInSelectedPermissionList(a))
          .map((a) => (
            <PermissionItem key={a.node.id} label={a.node.name} onClick={() => addMediaPermissionToList(a.node)} />
          ))}
      </div>
    </div>
  );
};

const GroupPermissions = ({
  name,
  a,
  canEdit,
  canDelete,
  canDownload,
  canShare,
  hires,
  onSelectPermissionSetting,
  removePermission,
}: GroupPermissionsProps): React.ReactElement | null => {
  if (a?.permissionsGroup === null) return null;
  const classes = styles();

  const onChangeSetting = (settingName: string, e: ChangeEvent<HTMLInputElement>): void => {
    onSelectPermissionSetting(settingName, e.target.checked);
  };

  return (
    <div className={classes.groupPermissionWrapper}>
      <div className={classes.groupPermissionItem}>
        <div className={classes.groupPermissionItemTop}>
          <div className={classes.groupPermissionItemTitle}>{name}</div>
          <button
            type="button"
            className={classes.groupPermissionItemRemoveButton}
            onClick={removePermission}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              font: 'inherit',
              color: 'inherit',
            }}
          >
            Remove
          </button>
        </div>
        <div>
          <CustomCheckBox onChange={(e) => onChangeSetting('canEdit', e)} defaultChecked={canEdit} label="Edit" />
          <CustomCheckBox onChange={(e) => onChangeSetting('canShare', e)} defaultChecked={canShare} label="Share" />
          <CustomCheckBox
            onChange={(e) => onChangeSetting('canDownload', e)}
            defaultChecked={canDownload}
            label="Download"
          />
          <CustomCheckBox onChange={(e) => onChangeSetting('canDelete', e)} defaultChecked={canDelete} label="Delete" />
          <CustomCheckBox onChange={(e) => onChangeSetting('hires', e)} defaultChecked={hires} label="Request Hires" />
        </div>
      </div>
    </div>
  );
};

const PermissionItem = ({ label, onClick }: PermissionItemProps): React.ReactElement => {
  const classes = styles();
  return (
    <button
      type="button"
      className={classes.checkBoxFormControl}
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        width: '100%',
        textAlign: 'left',
      }}
    >
      {label}
    </button>
  );
};

const CustomCheckBox = ({ label, ...rest }: CustomCheckBoxProps): React.ReactElement => {
  const classes = styles();
  return (
    <FormControlLabel
      className={classes.checkBoxFormControl}
      sx={{ color: 'text.primary' }}
      control={<Checkbox sx={{ color: 'text.primary', '&.Mui-checked': { color: 'primary.main' } }} {...rest} />}
      label={label}
    />
  );
};
